import { Send } from 'lucide-react';
import { useState } from 'react';

const APP_URL = import.meta.env.VITE_APP_URL || 'http://localhost:3000';
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error('Failed to send message');

      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div>
      <section className="py-20 bg-gradient-to-b from-filmspo-blue to-gray-900">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Get in Touch</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Have a question, feedback, or just want to say hello? We&apos;d love to hear from you.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-1">Contact Information</h2>
              {/* <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="w-6 h-6 text-filmspo-gold mt-1 shrink-0" />
                  <div>
                    <h3 className="font-semibold mb-1">Email Us</h3>
                    <a
                      href="mailto:hello@filmspo.com"
                      className="text-filmspo-blue-light hover:underline"
                    >
                      hello@filmspo.com
                    </a>
                    <p className="text-gray-500 text-sm mt-1">
                      We typically respond within 24 hours.
                    </p>
                  </div>
                </div>
              </div> */}

              <div className="mt-5 p-6 pl-0 rounded-lg">
                <h3 className="font-semibold mb-2">Looking for support?</h3>
                <p className="text-gray-600 text-sm mb-3">
                  If you already have a Filmspo account, you can reach our support team directly
                  from the app.
                </p>
                <a
                  href={`${APP_URL}/login`}
                  className="text-filmspo-blue-light hover:underline text-sm font-medium"
                >
                  Log in to your account &rarr;
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-filmspo-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-filmspo-green" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Message Sent</h2>
                  <p className="text-gray-600">
                    Thanks for reaching out. We&apos;ll get back to you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-filmspo-gold focus:border-transparent outline-none"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-filmspo-gold focus:border-transparent outline-none"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-filmspo-gold focus:border-transparent outline-none resize-y"
                      placeholder="How can we help?"
                    />
                  </div>

                  {error && <p className="text-red-500 text-sm">{error}</p>}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-filmspo-gold text-black px-6 py-3 rounded-lg font-semibold hover:bg-filmspo-gold-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
